import { Check, Copy, Plus, Server, Key } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useConvexAuth, useAction, useQuery } from "convex/react";
import { useSession } from "@/hooks/use-session";
import { format, formatDistanceToNow } from "date-fns";
import { usePostHog } from "posthog-js/react";
import { detectEnvironment, detectPlatform } from "@/logs/PosthogUtils";
import { useAppWorkspace } from "@/contexts/AppWorkspaceContext";
import { api } from "@/convex/src/_generated/api";
import { Id } from "@/convex/src/_generated/dataModel";

function ApiKeyCopyField({
  value,
  isCopied,
  onCopy,
  tooltip = "Copy to clipboard",
}: {
  value: string;
  isCopied: boolean;
  onCopy: () => void;
  tooltip?: string;
}) {
  return (
    <div className="relative w-full">
      <Input
        readOnly
        value={value}
        className="h-12 w-full rounded-lg border border-border/40 bg-background/50 font-mono text-sm tracking-wide text-foreground pr-16 shadow-sm focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary/20"
        style={{
          fontFamily:
            'ui-monospace, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
        }}
      />
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            onClick={onCopy}
            className="absolute right-3 top-3 h-6 w-6 p-0 rounded-md border-0 bg-transparent text-foreground/60 hover:text-foreground hover:bg-foreground/10 transition-all duration-200"
          >
            {isCopied ? (
              <Check className="h-4 w-4" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">{tooltip}</TooltipContent>
      </Tooltip>
    </div>
  );
}

export function ServerApiKeySection() {
  const [selectedServerId, setSelectedServerId] = useState<string | null>(null);
  const [newKeySecret, setNewKeySecret] = useState<string | null>(null);
  const [isKeyModalOpen, setIsKeyModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const { isAuthenticated, isLoading: isAuthLoading } = useConvexAuth();
  const { login } = useSession();
  const posthog = usePostHog();
  const { activeWorkspaceId } = useAppWorkspace();

  // Query for MCP servers in the workspace
  const servers = useQuery(
    api.appWorkspaceServers.listServers,
    isAuthenticated && activeWorkspaceId
      ? { workspaceId: activeWorkspaceId as Id<"appWorkspaces"> }
      : "skip",
  );

  // Query for existing server API keys
  const serverKeys = useQuery(
    api.apiKeys.listServerKeys,
    isAuthenticated && activeWorkspaceId
      ? { workspaceId: activeWorkspaceId as Id<"appWorkspaces"> }
      : "skip",
  );

  // Action to create server API key
  const createServerApiKey = useAction(api.apiKeys.createServerApiKey);

  const handleCopyKey = async () => {
    if (!newKeySecret) return;
    try {
      await navigator.clipboard.writeText(newKeySecret);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error("Clipboard error", err);
    }
  };

  const handleCreateKey = async () => {
    if (!isAuthenticated || !activeWorkspaceId || !selectedServerId) return;

    try {
      setIsCreating(true);
      setIsCopied(false);

      const result = await createServerApiKey({
        workspaceId: activeWorkspaceId as Id<"appWorkspaces">,
        appWorkspaceServerId: selectedServerId as Id<"appWorkspaceServers">,
      });

      setNewKeySecret(result.apiKey);
      setIsKeyModalOpen(true);

      posthog.capture("server_api_key_created", {
        platform: detectPlatform(),
        environment: detectEnvironment(),
      });
    } catch (err) {
      console.error("Failed to create server key", err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleCloseModal = (open: boolean) => {
    if (!open) {
      // Clear the secret key from memory when modal closes
      setNewKeySecret(null);
      setIsCopied(false);
    }
    setIsKeyModalOpen(open);
  };

  const describeTimestamp = (
    timestamp: number | null | undefined,
    emptyLabel = "Never",
  ) => {
    if (!timestamp) return emptyLabel;
    const date = new Date(timestamp);
    const relative = formatDistanceToNow(date, { addSuffix: true });
    return `${format(date, "PP")} (${relative})`;
  };

  const isLoading = isAuthLoading || servers === undefined;
  const hasServers = servers && servers.length > 0;
  const selectedServer = servers?.find((s) => s._id === selectedServerId);

  // Not authenticated
  if (!isAuthenticated && !isAuthLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold">Server API Keys</h3>
        </div>
        <div className="space-y-3 rounded-md border p-4">
          <p className="text-sm text-muted-foreground">
            Sign in to view and manage your server API keys.
          </p>
          <Button
            type="button"
            onClick={() => {
              posthog.capture("login_button_clicked", {
                location: "server_api_key_section",
                platform: detectPlatform(),
                environment: detectEnvironment(),
              });
              login();
            }}
            size="sm"
          >
            Sign in
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Key className="h-5 w-5 text-muted-foreground" />
          <h3 className="text-lg font-semibold">Server API Keys</h3>
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Server API keys are used by MCP servers to call Taias APIs (e.g.{" "}
          <code className="px-1 py-0.5 bg-muted rounded text-xs font-mono">
            /v1/flows/advise
          </code>
          ). Create a key for a specific server below.
        </p>
      </div>

      {/* Create new key section */}
      <div className="space-y-4 rounded-lg border border-border/50 bg-muted/30 p-4">
        <h4 className="text-sm font-medium">Create a new server key</h4>

        {isLoading ? (
          <div className="flex items-center gap-3">
            <div className="h-9 w-64 bg-muted animate-pulse rounded-md" />
            <div className="h-9 w-32 bg-muted animate-pulse rounded-md" />
          </div>
        ) : !hasServers ? (
          <div className="text-sm text-muted-foreground">
            <p>No MCP servers configured for this workspace.</p>
            <p className="mt-1">
              Add a server in the{" "}
              <a href="#servers" className="text-primary hover:underline">
                MCP Servers
              </a>{" "}
              tab first.
            </p>
          </div>
        ) : (
          <div className="flex items-center gap-3 flex-wrap">
            <Select
              value={selectedServerId ?? ""}
              onValueChange={(value) => setSelectedServerId(value || null)}
            >
              <SelectTrigger className="w-64">
                <SelectValue placeholder="Select an MCP server..." />
              </SelectTrigger>
              <SelectContent>
                {servers.map((server) => (
                  <SelectItem key={server._id} value={server._id}>
                    <div className="flex items-center gap-2">
                      <Server className="h-3.5 w-3.5 text-muted-foreground" />
                      <span>{server.name}</span>
                      {server.environment && (
                        <span className="text-xs text-muted-foreground">
                          ({server.environment})
                        </span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              type="button"
              onClick={handleCreateKey}
              disabled={!selectedServerId || isCreating}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              {isCreating ? "Creating..." : "Create Server Key"}
            </Button>
          </div>
        )}

        {selectedServer && (
          <p className="text-xs text-muted-foreground">
            A new API key will be created for{" "}
            <span className="font-medium">{selectedServer.name}</span>
            {selectedServer.environment && (
              <span className="text-muted-foreground">
                {" "}
                ({selectedServer.environment})
              </span>
            )}
          </p>
        )}
      </div>

      {/* Existing keys table */}
      {serverKeys && serverKeys.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium">Existing server keys</h4>
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Server</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Last Used</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {serverKeys.map((key) => (
                  <TableRow key={key._id}>
                    <TableCell className="font-medium">
                      {key.name ?? "Unnamed key"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Server className="h-3.5 w-3.5 text-muted-foreground" />
                        <span>{key.serverName ?? "Unknown"}</span>
                        {key.serverEnvironment && (
                          <span className="text-xs text-muted-foreground">
                            ({key.serverEnvironment})
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {describeTimestamp(key.createdAt)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {describeTimestamp(key.lastUsed, "Never used")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* New key modal */}
      <Dialog open={isKeyModalOpen} onOpenChange={handleCloseModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Server API Key Created</DialogTitle>
            <DialogDescription>
              Copy and store this key securely.{" "}
              <span className="font-semibold text-destructive">
                You will not be able to view it again.
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <ApiKeyCopyField
              value={newKeySecret ?? ""}
              isCopied={isCopied}
              onCopy={handleCopyKey}
            />
            <div className="rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground">
              <p className="font-medium mb-1">Usage:</p>
              <code className="block text-xs bg-background/50 p-2 rounded font-mono">
                curl -X POST https://your-taias-url/v1/flows/advise \<br />
                {"  "}-H "x-api-key:{" "}
                {newKeySecret
                  ? newKeySecret.substring(0, 12) + "..."
                  : "YOUR_KEY"}
                "
              </code>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
